import { create } from "zustand";
import { NavigateFunction } from "react-router-dom";
import { createApi } from "../../../utils/createApi";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import { AxiosError } from "axios";

interface DashboardState {
  // Dashboard totals
  dashboardTotal: any;
  setDashboardTotal: (data: any) => void;

  // Camera info
  camerasErrorInfo: any[];
  setCamerasErrorInfo: (data: any[] | ((prevState: any[]) => any[])) => void; // Allow function updater
  camerasWarningInfo: any[];
  setCamerasWarningInfo: (data: any[] | ((prevState: any[]) => any[])) => void; // Allow function updater

  // Tab state
  activeTab: string;
  setActiveTab: (tab: string) => void;

  // Pagination
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (rowsPerPage: number) => void;
  errorPaginationNumber: number;
  setErrorPaginationNumber: (num: number) => void;
  warningPaginationNumber: number;
  setWarningPaginationNumber: (num: number) => void;

  // Loading states
  pageLoading: boolean;
  setPageLoading: (
    loading: boolean | ((prevState: boolean) => boolean)
  ) => void;
  dataIsLoading: boolean;
  setDataIsLoading: (loading: boolean) => void;

  loadingErrorInfo: boolean;
  setLoadingErrorInfo: (loading: boolean) => void;
  loadingWarningInfo: boolean;
  setLoadingWarningInfo: (loading: boolean) => void;

  // UI states
  displayShowMoreButton: boolean;
  loadingMoreData: boolean;
  setLoadingMoreData: (loading: boolean) => void;

  // Data usage
  usageData: any;
  setUsageData: (data: any) => void;
  currentData: string;
  setCurrentData: (data: string) => void;
  dateRangeName: string;
  setDateRangeName: (name: string) => void;
  errorFetchingUsageData: boolean;
  setErrorFetchingUsageData: (error: boolean) => void;
  dateRangeString: string;
  setDateRangeString: (dateRangeString: string) => void;
  typeString: string;
  setTypeString: (typeString: string) => void;

  // Data usage graph specific state
  dataUsageOn: boolean;
  setDataUsageOn: (on: boolean) => void;
  last5Name: string;
  setLast5Name: (name: string) => void;
  rangeChoices: any[];
  setRangeChoices: (choices: any[]) => void;

  // False positive data
  falsePositiveData: any;
  setFalsePositiveData: (data: any) => void;
  noFPData: boolean;
  setNoFPData: (noData: boolean) => void;

  // API Actions
  getTotalCamerasInfo: (navigate: NavigateFunction) => Promise<void>;
  getFalsePositiveData: (navigate: NavigateFunction) => Promise<void>;
  getDataUsageData: (navigate: NavigateFunction) => Promise<void>;
  getAdditionalData: (navigate: NavigateFunction) => Promise<void>;
  updateShowMoreButtonState: () => void;
  resetState: () => void; // Add reset function
}

export const useDashboardStore = create<DashboardState>((set, get) => ({
  // Dashboard totals
  dashboardTotal: {},
  setDashboardTotal: (data) => set({ dashboardTotal: data }),

  // Camera info
  camerasErrorInfo: [],
  setCamerasErrorInfo: (data) => {
    const newState =
      typeof data === "function" ? data(get().camerasErrorInfo) : data;
    set({ camerasErrorInfo: newState });
  },
  camerasWarningInfo: [],
  setCamerasWarningInfo: (data) => {
    const newState =
      typeof data === "function" ? data(get().camerasWarningInfo) : data;
    set({ camerasWarningInfo: newState });
  },

  // Tab state
  activeTab: "error",
  setActiveTab: (tab) => set({ activeTab: tab }),

  // Pagination
  page: 0,
  setPage: (page) => set({ page }),
  rowsPerPage: 10,
  setRowsPerPage: (rowsPerPage) => set({ rowsPerPage }),
  errorPaginationNumber: 1,
  setErrorPaginationNumber: (num) => set({ errorPaginationNumber: num }),
  warningPaginationNumber: 1,
  setWarningPaginationNumber: (num) => set({ warningPaginationNumber: num }),

  loadingErrorInfo: true,
  setLoadingErrorInfo: (loading) => set({ loadingErrorInfo: loading }),
  loadingWarningInfo: true,
  setLoadingWarningInfo: (loading) => set({ loadingWarningInfo: loading }),

  // UI states
  displayShowMoreButton: false,
  loadingMoreData: false,
  setLoadingMoreData: (loading) => set({ loadingMoreData: loading }),

  // Loading states
  pageLoading: false,
  setPageLoading: (loading) =>
    set({
      pageLoading:
        typeof loading === "function" ? loading(get().pageLoading) : loading,
    }),
  dataIsLoading: true,
  setDataIsLoading: (loading) => set({ dataIsLoading: loading }),

  // Data usage
  usageData: [],
  setUsageData: (data) => set({ usageData: data }),
  currentData: "usageData",
  setCurrentData: (data) => set({ currentData: data }),
  dateRangeName: "Last 24 hours",
  setDateRangeName: (name) => set({ dateRangeName: name }),
  errorFetchingUsageData: false,
  setErrorFetchingUsageData: (error) => set({ errorFetchingUsageData: error }),
  dateRangeString: "last_24_hours",
  setDateRangeString: (dateRangeString) =>
    set({ dateRangeString: dateRangeString }),
  typeString: "bandwidth",
  setTypeString: (typeString) => set({ typeString: typeString }),

  // Data usage graph specific state
  dataUsageOn: false,
  setDataUsageOn: (on) => set({ dataUsageOn: on }),
  last5Name: "Data Usage",
  setLast5Name: (name) => set({ last5Name: name }),
  rangeChoices: [],
  setRangeChoices: (choices) => set({ rangeChoices: choices }),

  // False positive data
  falsePositiveData: null,
  setFalsePositiveData: (data) => set({ falsePositiveData: data }),
  noFPData: false,
  setNoFPData: (noData) => set({ noFPData: noData }),

  // API Actions
  getTotalCamerasInfo: async (navigate) => {
    const {
      setDashboardTotal,
      setCamerasErrorInfo,
      setCamerasWarningInfo,
      setActiveTab,
      activeTab,
      setLoadingErrorInfo,
      setLoadingWarningInfo,
    } = get();

    const AxiosUI = createApi(``);
    const retryFunction = () => {
      get().getTotalCamerasInfo(navigate);
    };

    try {
      const response = await AxiosUI.get(`camera/dashboard_total/`);
      setDashboardTotal(response.data);

      try {
        const errorResponse = await AxiosUI.get(
          `camera/dashboard/?status=error`
        );
        setCamerasErrorInfo(errorResponse.data.results);
        setLoadingErrorInfo(false);
        if (activeTab === "error") {
          setActiveTab("warning");
          setActiveTab("error");
        }
      } catch (reason) {
        universalAPIErrorHandler(
          reason as AxiosError,
          navigate,
          retryFunction,
          () => {
            setCamerasErrorInfo([]);
          }
        );
      }

      try {
        const warningResponse = await AxiosUI.get(
          `camera/dashboard/?status=warning`
        );
        setCamerasWarningInfo(warningResponse.data.results);
        setLoadingWarningInfo(false);
        if (activeTab === "warning") {
          setActiveTab("error");
          setActiveTab("warning");
        }
      } catch (reason) {
        universalAPIErrorHandler(
          reason as AxiosError,
          navigate,
          retryFunction,
          () => {
            setCamerasWarningInfo([]);
          }
        );
      }
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {}
      );
    }
  },

  getFalsePositiveData: async (navigate) => {
    const { setFalsePositiveData, setNoFPData } = get();
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      get().getFalsePositiveData(navigate);
    };

    try {
      const response = await AxiosUI.get(`analytics/false_positive/`);
      setFalsePositiveData(response.data);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setNoFPData(true);
        }
      );
    }
  },

  getDataUsageData: async (navigate) => {
    const {
      setUsageData,
      setDataIsLoading,
      setErrorFetchingUsageData,
      dateRangeString,
      typeString,
    } = get();
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      get().getDataUsageData(navigate);
    };
    setErrorFetchingUsageData(false);
    try {
      const response = await AxiosUI.get(
        `analytics/data_usage/?date_range=${dateRangeString}&order_by=${typeString}`
      );
      const updatedData = response.data;
      for (const key in updatedData) {
        const updatedKey: any = updatedData[key];
        updatedKey.abreviated_name = `${updatedKey.display_name.slice(
          0,
          7
        )}...`;
        if (updatedKey.kbs) {
          const updatedKBS = Math.round(updatedKey.kbs / 10000) / 100;
          updatedKey.kbs = updatedKBS;
        }
      }
      setUsageData(updatedData);
      setDataIsLoading(false);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setUsageData([]);
          setErrorFetchingUsageData(true);
          setDataIsLoading(false);
        }
      );
    }
  },

  getAdditionalData: async (navigate) => {
    const {
      activeTab,
      errorPaginationNumber,
      setErrorPaginationNumber,
      setCamerasErrorInfo,
      warningPaginationNumber,
      setWarningPaginationNumber,
      setCamerasWarningInfo,
    } = get();

    // Set loading state
    set({ loadingMoreData: true, displayShowMoreButton: false });

    const AxiosUI = createApi(``);

    const retryFunction = () => {
      get().getAdditionalData(navigate);
    };

    if (activeTab === "error") {
      try {
        const response = await AxiosUI.get(
          `camera/dashboard/?page=${errorPaginationNumber + 1}&status=error`
        );
        setErrorPaginationNumber(errorPaginationNumber + 1);
        setCamerasErrorInfo((previousState: any) => [
          ...previousState,
          ...response.data.results,
        ]);

        // Update UI states
        set({ loadingMoreData: false });

        // Update displayShowMoreButton based on response
        const shouldShowMoreButton = response.data.results.length === 100;
        set({ displayShowMoreButton: shouldShowMoreButton });
      } catch (reason) {
        universalAPIErrorHandler(
          reason as AxiosError,
          navigate,
          retryFunction,
          () => {
            set({ loadingMoreData: false, displayShowMoreButton: false });
          }
        );
      }
    } else if (activeTab === "warning") {
      try {
        const response = await AxiosUI.get(
          `camera/dashboard/?page=${warningPaginationNumber + 1}&status=warning`
        );
        setWarningPaginationNumber(warningPaginationNumber + 1);
        setCamerasWarningInfo((previousState: any) => [
          ...previousState,
          ...response.data.results,
        ]);

        // Update UI states
        set({ loadingMoreData: false });

        // Update displayShowMoreButton based on response
        const shouldShowMoreButton = response.data.results.length === 100;
        set({ displayShowMoreButton: shouldShowMoreButton });
      } catch (reason) {
        universalAPIErrorHandler(
          reason as AxiosError,
          navigate,
          retryFunction,
          () => {
            set({ loadingMoreData: false, displayShowMoreButton: false });
          }
        );
      }
    }
  },

  // Add this function after getAdditionalData
  updateShowMoreButtonState: () => {
    const {
      activeTab,
      page,
      rowsPerPage,
      errorPaginationNumber,
      warningPaginationNumber,
      camerasErrorInfo,
      camerasWarningInfo,
    } = get();

    const currentData =
      activeTab === "error" ? camerasErrorInfo : camerasWarningInfo;
    const currentPaginationNumber =
      activeTab === "error" ? errorPaginationNumber : warningPaginationNumber;

    // Ensure data exists before checking length
    if (!currentData || currentData.length === 0) {
      set({ displayShowMoreButton: false });
      return;
    }

    // Logic to show button:
    // 1. If the total items aren't a multiple of 100, it means we've loaded all pages from the backend for this chunk.
    // 2. Or, if the current page index + 1 equals the total number of pages fetched so far.
    const morePagesPotentiallyAvailable = currentData.length % 100 === 0;
    let showButton = false;

    if (morePagesPotentiallyAvailable) {
      const totalPagesFetched = currentPaginationNumber;
      const currentPageIndex = page; // page is 0-based index

      if (rowsPerPage === 100) {
        showButton = totalPagesFetched === currentPageIndex + 1;
      } else if (rowsPerPage === 25) {
        // Show if the last item on the current page is the last item fetched so far
        showButton =
          (currentPageIndex + 1) * 25 === currentData.length &&
          totalPagesFetched * 100 === currentData.length;
      } else if (rowsPerPage === 10) {
        // Show if the last item on the current page is the last item fetched so far
        showButton =
          (currentPageIndex + 1) * 10 === currentData.length &&
          totalPagesFetched * 100 === currentData.length;
      }
    }

    set({ displayShowMoreButton: showButton });
  },

  // Add this function to reset all state to initial values
  resetState: () =>
    set({
      dashboardTotal: {},
      camerasErrorInfo: [],
      camerasWarningInfo: [],
      activeTab: "error",
      page: 0,
      rowsPerPage: 10,
      errorPaginationNumber: 1,
      warningPaginationNumber: 1,
      displayShowMoreButton: false,
      loadingMoreData: false,
      pageLoading: false,
      dataIsLoading: true,
      usageData: [],
      currentData: "usageData",
      dateRangeName: "Last 24 hours",
      dataUsageOn: false,
      last5Name: "Data Usage",
      noFPData: false,
      errorFetchingUsageData: false,
      loadingErrorInfo: true,
      loadingWarningInfo: true,
    }),
}));
